/**
 * Aba "Conexão": o token do site (mostrado uma vez só), o download do plugin, o
 * que o plugin informou sobre a instalação e os passos para ligar um site novo.
 *
 * O embed para site que não é WordPress tem chave própria e outro contrato
 * (ADR 0024, D-i); entra nesta aba quando a API dele existir.
 */
import { useState } from 'react';

import { Badge, Button } from '@/components/atoms';
import { useAgora } from '@/hooks/useAgora';
import { useAtualizacaoAutomatica, useGerarToken, useRevogarToken } from '@/hooks/usePortal';
import { formatarDataHora } from '@/i18n/formato';
import { API_BASE } from '@/services/api';
import { baixarBlob, portalApi } from '@/services/portal';
import type { PortalSiteDetalhe, TokenGerado } from '@/types/portal';
import {
  compararSemver,
  descreverIdade,
  rotuloDaColuna,
  segundosDesde,
  urlPublicaDaApiDeDados,
} from '@/utils/portal';

import { CampoInterruptor } from './campos';
import styles from './Portal.module.css';

const FORMATO_DATA: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

function copiar(texto: string): void {
  void navigator.clipboard?.writeText(texto);
}

function CartaoToken({ detalhe }: { detalhe: PortalSiteDetalhe }) {
  const gerar = useGerarToken(detalhe.site_id);
  const revogar = useRevogarToken(detalhe.site_id);
  const [novo, setNovo] = useState<TokenGerado | null>(null);
  const [falha, setFalha] = useState<string | null>(null);
  const agora = useAgora();
  const t = detalhe.token;

  const gerarToken = async () => {
    if (
      t &&
      !window.confirm(
        'Gerar um token novo? O atual para de funcionar na hora e o plugin deste site fica sem ler o manifesto até receber o novo.',
      )
    ) {
      return;
    }
    setFalha(null);
    try {
      setNovo(await gerar.mutateAsync());
    } catch {
      setFalha('Não foi possível gerar o token agora.');
    }
  };

  const revogarToken = async () => {
    if (
      !window.confirm(
        'Revogar o token? O plugin deste site para de ler o manifesto na hora e não volta sem um token novo.',
      )
    ) {
      return;
    }
    setFalha(null);
    try {
      await revogar.mutateAsync();
      setNovo(null);
      gerar.reset();
    } catch {
      setFalha('Não foi possível revogar o token agora.');
    }
  };

  return (
    <section className={styles.cartao} aria-label="Token do site">
      <div className={styles.cartaoCabeca}>
        <div>
          <h3 className={styles.cartaoTitulo}>Token do site</h3>
          <p className={styles.cartaoResumo}>
            O plugin usa este token para ler o manifesto. Cada site tem o seu: uma emissora não lê a
            configuração de outra.
          </p>
        </div>
        {t ? <Badge tone="ok">Ativo</Badge> : <Badge tone="warn">Sem token</Badge>}
      </div>
      <div className={styles.cartaoCorpo}>
        {novo && (
          <div className={styles.tokenCaixa} role="status">
            <strong>Token criado. Copie agora: ele não aparece de novo.</strong>
            <code className={styles.token}>{novo.token}</code>
            <div className={styles.cartaoRodape} style={{ borderTop: 'none', paddingTop: 0 }}>
              <Button type="button" size="sm" onClick={() => copiar(novo.token)}>
                Copiar token
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setNovo(null);
                  gerar.reset(); // o segredo também sai da memória da mutação
                }}
              >
                Já guardei
              </Button>
            </div>
          </div>
        )}
        {t && (
          <dl className={styles.dados}>
            <dt>Prefixo</dt>
            <dd className={styles.mono}>{t.prefixo}</dd>
            <dt>Criado em</dt>
            <dd>{formatarDataHora(t.criado_em, FORMATO_DATA)}</dd>
            <dt>Último uso</dt>
            <dd>
              {t.ultimo_uso_em
                ? `${formatarDataHora(t.ultimo_uso_em, FORMATO_DATA)} (${descreverIdade(segundosDesde(t.ultimo_uso_em, agora))})`
                : 'nunca usado'}
            </dd>
          </dl>
        )}
        {falha && (
          <div role="alert" className={styles.avisoCrit}>
            {falha}
          </div>
        )}
        <div className={styles.cartaoRodape}>
          <Button
            type="button"
            size="sm"
            loading={gerar.isPending}
            onClick={() => void gerarToken()}
          >
            {t ? 'Gerar novo token' : 'Gerar token'}
          </Button>
          {t && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              loading={revogar.isPending}
              onClick={() => void revogarToken()}
            >
              Revogar token
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

function CartaoPlugin({ detalhe }: { detalhe: PortalSiteDetalhe }) {
  const auto = useAtualizacaoAutomatica(detalhe.site_id);
  const [baixando, setBaixando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const oferta = detalhe.plugin;

  const baixar = async () => {
    setBaixando(true);
    setFalha(null);
    try {
      const { blob, nome } = await portalApi.pacote();
      baixarBlob(blob, nome);
    } catch {
      setFalha('Não foi possível baixar o plugin agora.');
    } finally {
      setBaixando(false);
    }
  };

  return (
    <section className={styles.cartao} aria-label="Plugin do WordPress">
      <div className={styles.cartaoCabeca}>
        <div>
          <h3 className={styles.cartaoTitulo}>Plugin do WordPress</h3>
          <p className={styles.cartaoResumo}>
            Pacote assinado, servido pelo próprio WIREDESK: funciona sem depender de repositório
            externo.
          </p>
        </div>
        <Badge tone="info">Versão {oferta.versao_atual}</Badge>
      </div>
      <div className={styles.cartaoCorpo}>
        <dl className={styles.dados}>
          <dt>Versão atual</dt>
          <dd className={styles.mono}>{oferta.versao_atual}</dd>
          <dt>Versão mínima</dt>
          <dd className={styles.mono}>{oferta.versao_minima}</dd>
        </dl>
        <CampoInterruptor
          rotulo="Atualizar o plugin sozinho quando sair versão nova"
          valor={oferta.atualizacao_automatica}
          disabled={auto.isPending}
          dica="Desligado por padrão. Ligado, o WordPress baixa e instala o pacote assinado."
          onChange={(ligada) => auto.mutate(ligada)}
        />
        {auto.isError && (
          <div role="alert" className={styles.avisoCrit}>
            Não foi possível mudar a atualização automática agora.
          </div>
        )}
        {falha && (
          <div role="alert" className={styles.avisoCrit}>
            {falha}
          </div>
        )}
        <div className={styles.cartaoRodape}>
          <Button type="button" size="sm" loading={baixando} onClick={() => void baixar()}>
            Baixar o plugin (.zip)
          </Button>
        </div>
      </div>
    </section>
  );
}

function CartaoInstalacao({ detalhe }: { detalhe: PortalSiteDetalhe }) {
  const agora = useAgora();
  const inst = detalhe.instalacao;
  return (
    <section className={styles.cartao} aria-label="Instalação no site">
      <div className={styles.cartaoCabeca}>
        <div>
          <h3 className={styles.cartaoTitulo}>Instalação no site</h3>
          <p className={styles.cartaoResumo}>
            O que o plugin informa a cada leitura do manifesto. Nunca inclui endereço do site nem
            dado de usuário.
          </p>
        </div>
      </div>
      <div className={styles.cartaoCorpo}>
        {!inst ? (
          <p className={styles.vazio}>O plugin ainda não leu o manifesto deste site.</p>
        ) : (
          <dl className={styles.dados}>
            <dt>Último contato</dt>
            <dd>
              {descreverIdade(segundosDesde(inst.ultima_leitura_em, agora))} (
              {formatarDataHora(inst.ultima_leitura_em, FORMATO_DATA)})
            </dd>
            <dt>Plugin</dt>
            <dd>
              {inst.versao}
              {compararSemver(inst.versao, detalhe.plugin.versao_atual) < 0 && (
                <>
                  {' '}
                  <Badge tone="warn" size="xs">
                    Há versão nova: {detalhe.plugin.versao_atual}
                  </Badge>
                </>
              )}
            </dd>
            <dt>WordPress</dt>
            <dd>{inst.wp ?? 'não informado'}</dd>
            <dt>PHP</dt>
            <dd>{inst.php ?? 'não informado'}</dd>
            <dt>Tema ativo</dt>
            <dd className={styles.mono}>{inst.tema ?? 'não informado'}</dd>
            <dt>Plugin de SEO</dt>
            <dd>{inst.seo ?? 'não informado'}</dd>
            <dt>Colunas servidas agora</dt>
            <dd>
              {inst.servidas.length > 0
                ? inst.servidas.map(rotuloDaColuna).join(', ')
                : 'nenhuma (modo sombra)'}
            </dd>
            <dt>Rotas em conflito</dt>
            <dd>
              {inst.conflitos.length > 0 ? (
                <span className={styles.mono}>{inst.conflitos.join(', ')}</span>
              ) : (
                'nenhuma'
              )}
            </dd>
          </dl>
        )}
      </div>
    </section>
  );
}

function CartaoPassos({ detalhe }: { detalhe: PortalSiteDetalhe }) {
  const urlApi = urlPublicaDaApiDeDados(API_BASE, window.location.origin);
  return (
    <section className={styles.cartao} aria-label="Como ligar um site">
      <div className={styles.cartaoCabeca}>
        <div>
          <h3 className={styles.cartaoTitulo}>Como ligar um site WordPress</h3>
          <p className={styles.cartaoResumo}>Do zero à primeira coluna no ar: sem editar código.</p>
        </div>
      </div>
      <div className={styles.cartaoCorpo}>
        <ol className={styles.passos}>
          <li>
            Baixe o plugin acima e envie o .zip em Plugins, Adicionar novo, Enviar plugin, no
            wp-admin.
          </li>
          <li>Ative o plugin e abra Ajustes, WIREDESK Portal.</li>
          <li>
            Em "URL da API de dados", cole <code className={styles.mono}>{urlApi}</code>{' '}
            <Button type="button" size="sm" variant="ghost" onClick={() => copiar(urlApi)}>
              Copiar endereço
            </Button>
          </li>
          <li>
            Cole o token deste site e clique em Testar. A página de saúde deve mostrar o manifesto
            lido há poucos segundos.
          </li>
          <li>
            Volte à aba Colunas, ligue uma coluna por vez e mude "Quem serve" para o plugin quando a
            Conferência estiver em dia. Site: <span className={styles.mono}>{detalhe.url}</span>
          </li>
        </ol>
      </div>
    </section>
  );
}

export function AbaConexao({ detalhe }: { detalhe: PortalSiteDetalhe }) {
  return (
    <div>
      <CartaoToken detalhe={detalhe} />
      <CartaoPlugin detalhe={detalhe} />
      <CartaoInstalacao detalhe={detalhe} />
      <CartaoPassos detalhe={detalhe} />
    </div>
  );
}
